import { rnd } from '@edicom/common/string/rnd';
import { htmlEscape } from '@edicom/common/string/htmlEscape';
import { string as objectToString } from '@edicom/common/object/to/string';

/**
 * Devuelve la clase FbPopupCellEditor, para que el grid la instancie cuando pinta la celda en modo edición.
 * Esta clase centraliza metodos para usar el popup del grid, renderiza en especial la directiva <fb-grid-popup>
 *
 * @param {FbCellEditor} FbCellEditor se pasa la clase como injección de dependencia
 * @param {Object} options opciones que se pasan de clase en clase (FbPopupCellEditor -> FbCellEditor -> EcCellEditor)
 * @param {Object} methods metodos que se usan para personalizar la celda.
 * @param {function} [methods.isOpened] permite forzar a que el popup este abierto al iniciar la edición
 * @param {function} [methods.renderCell] debe devolver el contenido html visible de la celda, se llama con ({value, data}),
 * @param {function} methods.renderPopup debe devolver el contenido html del popup, se llama con ({value, data})
 * @returns {FbPopupCellEditor}
 */

const defaultOptions = { ecCellName: 'FbPopupCellEditor' };

export function FbPopupCellEditorClass(FbCellEditor, options, { isOpened = false, renderCell = toHtml, renderPopup } = {}) {
  return class FbPopupCellEditor extends FbCellEditor {
    constructor(currentOptions = {}) {
      super(Object.assign({}, defaultOptions, options, currentOptions));

      if (typeof renderPopup !== 'function') {
        throw new Error('FbPopupCellEditor: "popup.templateFn" obligatorio');
      }

      // ahora que esta sobreescrito (o no), podemos hacer el bind:
      this.onClickCell = this.onClickCell.bind(this);
    }

    render({ value, data, original }) {
      const attrOpened = isOpened || this.keyPress === 'click:icon' || this.keyPress === 13 /*enter*/ ? `opened` : ``;
      const { fullscreen = false, xPath } = FbCellEditor.getMetadata(original);
      const id = `FbPopupCellEditor_id_${original.model}_${rnd()}`;
      const icon = fullscreen ? 'up-right-and-down-left-from-center' : 'bars';
      const attrFullscreen = fullscreen ? `fullscreen` : ``;
      const attrLabel = original.title ? `label="${original.title}"` : ``;

      return `
        <template id="${id}_template">${renderPopup({ value, data })}</template>
        ${this.renderEcCellRenderer({ id, icon, name: this.ecCellName, value: renderCell({ value, data }), iconDelete: true })}
        <fb-grid-popup for="${id}" ${attrFullscreen} ${attrOpened} ${attrLabel} x-path="${xPath}"></fb-grid-popup>
      `;
    }

    postrender() {
      this.eCellRendererValue = this.eCellEditor.querySelector('ec-cell-renderer-value');
      this.eCellPopup = this.eCellEditor.querySelector('fb-grid-popup');

      this.eCellEditor.addEventListener('click', this.onClickCell);
      this.eIcon = this.eCellEditor.querySelector('[on-click-cell="icon"]');
      if (this.eIcon) {
        if (!isOpened) {
          this.eIcon.focus();
        }
        this.onKeyDownIcon = this.onKeyDownIcon.bind(this);
        this.eIcon.addEventListener('keydown', this.onKeyDownIcon);
      }
    }

    onChange({ value, data }) {
      this.eCellRendererValue.innerHTML = renderCell({ value, data });
    }

    destroy() {
      super.destroy();
      this.eCellEditor && this.eCellEditor.removeEventListener('click', this.onClickCell);
      this.eIcon && this.eIcon.removeEventListener('keydown', this.onKeyDownIcon);
    }

    onClickCell(event) {
      if (event.target.closest('[on-click-cell="icon"]')) {
        return this.eCellPopup.toggle();
      }
      if (event.target.closest('[ec-icon-action]')) {
        this.resetValue();
      }
    }

    onKeyDownIcon(event) {
      if (event.key === 'Enter') {
        event.stopPropagation();
        return this.eCellPopup.toggle();
      }
    }
  };
}

/**
 * Devuelve el valor de la celda transformado en html.
 * @param {Object} value el valor de la celda
 * @returns {string}
 */
function toHtml({ value } = {}) {
  return htmlEscape(objectToString(value));
}
